/**
 * Chart colours and axis options for the survey result charts.
 *
 * chart.js draws to a canvas, so it cannot be handed a `var()` the way the
 * SVG gauges are — the custom property has to be resolved to a concrete colour
 * first. Reading it from `:root` keeps the charts on the active theme instead
 * of pinning them to one brand, which a literal would do.
 *
 * Two kinds of colour, two rules:
 *  - a series is brand colour, so it uses the theme primary, matching the
 *    compliance chart on the dashboard;
 *  - a score is meaning, so the gauges and question bars use the severity
 *    tokens instead. See `score-color.util.ts`.
 */

/** Resolves a CSS custom property from `:root`, falling back if it is unset. */
export function cssColor(token: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim();

  return value.length > 0 ? value : fallback;
}

/** Line and bar colour for a result series. */
export function seriesColor(): string {
  return cssColor('--color-primary-500', '#91268f');
}

/** Translucent fill under a line series. */
export function seriesFillColor(): string {
  return cssColor('--color-primary-opacity-10', 'rgba(145, 38, 143, 0.1)');
}

/** Grid line colour. */
export function gridColor(): string {
  return cssColor('--p-content-border-color', '#e5e7eb');
}

/**
 * Axis options shared by every result chart. The y axis is pinned to the full
 * answer scale so a narrow spread does not read as a dramatic one.
 */
export function scoreScaleOptions(max: number): any {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    },
    scales: {
      y: {
        min: 0,
        max: max,
        ticks: { stepSize: 1 },
        grid: { color: gridColor() }
      },
      x: {
        grid: { display: false }
      }
    }
  };
}

/** Bar dataset for a set of scores. */
export function scoreBarDataset(label: string, data: number[]): any {
  return {
    label,
    data,
    backgroundColor: seriesColor(),
    borderRadius: 4,
    maxBarThickness: 48
  };
}

/** Line dataset for a set of scores over time. */
export function scoreLineDataset(label: string, data: number[]): any {
  return {
    label,
    data,
    borderColor: seriesColor(),
    backgroundColor: seriesFillColor(),
    pointBackgroundColor: seriesColor(),
    borderWidth: 2,
    tension: 0.3,
    fill: true
  };
}
